import React, { useState, useEffect, useRef, useCallback } from "react";
import { View, ScrollView, StyleSheet } from "react-native";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import { DownArrow } from "../../../components/svgs/common";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import ProcessingMessageBox from "../../../components/chat/LoadingMessage/ProcessingMessageBox";
import { useFocusEffect } from "@react-navigation/native";
import { AITutorMessageBubble } from "../../../components/chatNewInterface/interfaceComponents/messageBubble/AITutorMessageBubble";
import { Message } from "../../../graphql/generated/graphql";
import { AttachmentChipProps } from "../../../components/chatNewInterface/interfaceComponents/attachmentChip/AttachmentChip";
import type { MessageItem } from "../../companion/MessagesContainer";

const AITUTOR_MESSAGES_MAX_WIDTH = 704;
const DEFAULT_CONTAINER_PADDING_BOTTOM = 88;

interface AITutorMessagesContainerProps {
  messages: MessageItem[];
  isStreaming?: boolean;
  currentStreamingMessageId?: string | null;
  resetAutoScroll?: number;
  chatInputHeight?: number;
  shouldRevealFirstMessageAtTop?: boolean;
  onRevealedFirstMessageAtTop?: () => void;
}

const mapMessageToBubble = (message: Partial<Message>) => ({
  role: (message?.sender === "human" ? "user" : "assistant") as "user" | "assistant",
  content: message?.content ?? "",
  timestamp: message?.created ? new Date(message.created) : new Date(),
  isArchived: false,
});

const AITutorMessagesContainer = ({
  messages,
  isStreaming = false,
  currentStreamingMessageId,
  resetAutoScroll,
  chatInputHeight = DEFAULT_CONTAINER_PADDING_BOTTOM,
  shouldRevealFirstMessageAtTop,
  onRevealedFirstMessageAtTop,
}: AITutorMessagesContainerProps) => {
  const scrollViewRef = useRef<ScrollView | null>(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [downButton, setDownButton] = useState(false);
  const [humanMessageOffset, setHumanMessageOffset] = useState(0);
  const [scrollViewHeight, setScrollViewHeight] = useState<number | undefined>(undefined);
  const [parentHeight, setParentHeight] = useState(0);
  const [isUserInteracted, setIsUserInteracted] = useState(false);
  const isAtBottomRef = useRef(true);
  const isProgrammaticScrollRef = useRef(false);

  useFocusEffect(
    useCallback(() => {
      return () => setIsUserInteracted(false);
    }, [])
  );

  const handleScroll = useCallback(
    (event: {
      nativeEvent: {
        layoutMeasurement: { height: number };
        contentOffset: { y: number };
        contentSize: { height: number };
      };
    }) => {
      const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
      const isAtBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 50;
      if (isProgrammaticScrollRef.current) {
        if (isAtBottom) {
          setIsUserScrolling(false);
          setDownButton(false);
        }
        isAtBottomRef.current = isAtBottom;
        return;
      }
      if (!isAtBottom) {
        setIsUserScrolling(true);
        setDownButton(true);
        setIsUserInteracted(true);
      } else {
        setIsUserScrolling(false);
        setDownButton(false);
      }
      isAtBottomRef.current = isAtBottom;
    },
    []
  );

  const scrollToBottom = (animated = false) => {
    if (shouldRevealFirstMessageAtTop) {
      return;
    }
    isProgrammaticScrollRef.current = true;
    scrollViewRef.current?.scrollToEnd({ animated });
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 0);
  };

  const scrollToBottomButton = () => {
    setDownButton(false);
    scrollToBottom(true);
  };

  const scrollToTop = useCallback((animated = true) => {
    isProgrammaticScrollRef.current = true;
    scrollViewRef.current?.scrollTo({ y: 0, animated });
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 0);
  }, []);

  useEffect(() => {
    if (!shouldRevealFirstMessageAtTop) {
      return;
    }
    scrollToTop(true);
    const delayMs = 500;
    const timeoutId = setTimeout(() => {
      onRevealedFirstMessageAtTop?.();
    }, delayMs);
    return () => clearTimeout(timeoutId);
  }, [shouldRevealFirstMessageAtTop, scrollToTop, onRevealedFirstMessageAtTop]);

  useEffect(() => {
    const el = scrollViewRef.current as (ScrollView & { setAttribute?: (a: string, b: string) => void }) | null;
    if (el?.setAttribute) el.setAttribute("data-sensitive", "true");
  }, []);

  useEffect(() => {
    if (resetAutoScroll !== undefined) {
      setIsUserInteracted(false);
      setScrollViewHeight(undefined);
      setHumanMessageOffset(0);
    }
  }, [resetAutoScroll]);

  useEffect(() => {
    if (messages.length === 0) {
      setScrollViewHeight(undefined);
      setHumanMessageOffset(0);
    }
  }, [messages.length]);

  useEffect(() => {
    if (isUserInteracted && humanMessageOffset > 0 && !isUserScrolling) {
      isProgrammaticScrollRef.current = true;
      scrollViewRef.current?.scrollTo({ y: humanMessageOffset });
      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 0);
    } else {
      scrollToBottom();
    }
  }, [humanMessageOffset]);

  useEffect(() => {
    if (!messages?.length) return;
    if (!isUserInteracted) scrollToBottom();
  }, [messages]);

  const handleOnLayout = (
    event: { nativeEvent: { layout: { y: number } } },
    item: MessageItem,
    array: MessageItem[]
  ) => {
    const isLastHumanMessage = [...array].reverse().find((m) => m.sender === "human")?.id === item.id;
    if (isLastHumanMessage) {
      const y = event.nativeEvent.layout.y;
      setHumanMessageOffset(y);
      setScrollViewHeight(y + parentHeight - chatInputHeight);
    }
  };

  useEffect(() => {
    if (humanMessageOffset > 0) {
      setScrollViewHeight(humanMessageOffset + parentHeight - chatInputHeight);
    }
  }, [chatInputHeight, humanMessageOffset, parentHeight]);

  const streamingMessage = currentStreamingMessageId
    ? messages.find((m) => m.id === currentStreamingMessageId)
    : undefined;
  const shouldShowProcessingFallback = isStreaming && (!streamingMessage || !streamingMessage.content);

  const visibleMessages = messages?.filter((item) => {
    if (isStreaming && item.id === currentStreamingMessageId && item.sender === "ai" && !item.content?.trim()) {
      return false;
    }
    return true;
  });

  return (
    <View
      style={[styles.outerContainer, { paddingBottom: chatInputHeight }]}
      onLayout={(e) => setParentHeight(e.nativeEvent.layout.height)}>
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        maintainVisibleContentPosition={{ minIndexForVisible: 0, autoscrollToTopThreshold: 10 }}
        showsVerticalScrollIndicator
        contentContainerStyle={[styles.scrollContent, { height: scrollViewHeight }]}
        onContentSizeChange={() => {
          if (!isUserInteracted) scrollToBottom();
        }}
        tabIndex={0}>
        <View style={styles.contentWrapper}>
          <View style={styles.messagesColumn}>
            {visibleMessages?.map((item, index, array) => {
              const attachmentItems: AttachmentChipProps[] = [];
              for (const todo of item.todos ?? []) {
                attachmentItems.push({ type: "task", name: todo.name ?? "", duedate: todo.date });
              }
              for (const assignment of item.assignments ?? []) {
                attachmentItems.push({
                  type: "assignment",
                  name: assignment.name ?? "",
                  duedate: assignment.due_date,
                });
              }
              for (const file of item.files ?? []) {
                attachmentItems.push({ type: "file", name: file.fileName ?? "" });
              }
              return (
                <View key={String(item.id ?? index)} onLayout={(e) => handleOnLayout(e, item, array)}>
                  <AITutorMessageBubble
                    {...mapMessageToBubble(item)}
                    taskAttachmentChips={attachmentItems}
                    isFullWidth
                  />
                </View>
              );
            })}
            {shouldShowProcessingFallback && (
              <View>
                <CustomSpacing type="vertical" size="s" />
                <ProcessingMessageBox />
              </View>
            )}
          </View>
        </View>
      </ScrollView>
      {downButton && (
        <View style={styles.buttonContainer}>
          <CustomButton
            styleType="primary"
            style={styles.button}
            leftIcon={<DownArrow />}
            onPress={scrollToBottomButton}
            rightIcon={undefined}
            text={undefined}
            textProps={undefined}
            textStyle={undefined}
            disabled={false}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    width: "100%",
    paddingHorizontal: 0,
  },
  scrollView: {
    flex: 1,
    width: "100%",
    zIndex: 0,
    paddingHorizontal: 0,
  },
  scrollContent: {
    paddingTop: 20,
    paddingHorizontal: 0,
  },
  contentWrapper: {
    width: "100%",
    alignItems: "center",
    paddingHorizontal: 0,
  },
  messagesColumn: {
    width: "100%",
    maxWidth: AITUTOR_MESSAGES_MAX_WIDTH,
    paddingHorizontal: 0,
    paddingBottom: 40,
  },
  buttonContainer: {
    width: "100%",
    alignItems: "center",
  },
  button: {
    justifyContent: "center",
    position: "absolute",
    alignItems: "center",
    bottom: 10,
    width: 30,
    height: 30,
    borderRadius: 25,
    zIndex: 5,
  },
});

export default AITutorMessagesContainer;
